import axios from 'axios';
import { ApiError, isApiErrorHandled, markApiErrorHandled, normalizeApiError, } from '@/shared/api/errors';
import { AUTH_TOKEN_STORAGE_KEY } from '@/shared/config/auth';
import { appEnv } from '@/shared/config/env';
import { reportClientError } from '@/shared/lib/error/reportClientError';
/** 后端统一响应外壳中表示成功的业务状态码。 */
export const API_SUCCESS_CODE = 200;
/** 后端统一响应外壳中表示会话失效的业务状态码。 */
export const API_UNAUTHORIZED_CODE = 401;
/** 默认从浏览器本地存储读取令牌，服务端渲染或测试环境返回空值。 */
function readStoredAuthToken() {
    if (typeof window === 'undefined')
        return null;
    return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
}
/** 当前请求层认证令牌读取策略。 */
let authTokenReader = readStoredAuthToken;
/** 可选的 401 恢复处理器，避免 shared 层依赖具体路由或用户状态实现。 */
let unauthorizedHandler = null;
/** 可选的接口失败提示器，避免 shared 层依赖具体 UI 组件库。 */
let apiErrorNotifier = null;
/** 配置认证令牌读取方式，登录模块接入后在 app 启动阶段调用。 */
export function configureAuthTokenReader(reader) {
    authTokenReader = reader;
}
/** 配置认证失效后的恢复动作，通常由 app 层清理会话并跳转登录页。 */
export function configureUnauthorizedHandler(handler) {
    unauthorizedHandler = handler;
}
/** 配置全局错误提示，由 app 层在启动阶段注入全局提示实现。 */
export function configureApiErrorNotifier(notifier) {
    apiErrorNotifier = notifier;
}
/** 把错误投递给已注册的提示出口；尚未注册时落到统一错误上报，避免失败被完全吞掉。 */
function deliverApiError(input, options) {
    if (apiErrorNotifier === null) {
        reportClientError('api-error', normalizeApiError(input));
        return;
    }
    apiErrorNotifier(input, options);
}
/**
 * 全站统一的错误提示出口。
 *
 * 业务代码捕获到错误后统一调用它提示，而不是各自调用 message.error：
 * 出口唯一，同一失败天然不会弹两次。传入错误对象时会同时标记为已处理，
 * 请求层的自动兜底便不会再重复弹出；传入字符串则用于本地校验等已归一化的文案。
 *
 * @param input 接口错误对象，或已归一化的错误文案。
 * @param options 文案控制，见 {@link NotifyApiErrorOptions}。
 */
export function notifyApiError(input, options) {
    markApiErrorHandled(input);
    deliverApiError(input, options);
}
/**
 * 请求层的自动兜底：把调用方没有自行处理的接口失败交给统一出口。
 *
 * 延后一个宏任务再判断：调用方在 `await` 之后的微任务里调用 `notifyApiError` 时，
 * 这里尚未投递，于是不会重复提示同一次失败。
 * 取消的请求和会话失效不提示：前者是主动放弃，后者由登录恢复流程负责反馈。
 *
 * @param error 已归一化的请求错误。
 */
export function notifyApiErrorIfUnhandled(error) {
    if (isApiErrorHandled(error))
        return;
    const normalizedError = normalizeApiError(error);
    if (normalizedError.kind === 'cancelled' || normalizedError.status === 401)
        return;
    setTimeout(() => {
        if (apiErrorNotifier === null || isApiErrorHandled(error))
            return;
        apiErrorNotifier(error);
    }, 0);
}
/**
 * 调用方自行承载失败反馈（内联文案或局部状态）时调用。
 *
 * 统一出口会延后一个宏任务弹 toast，因此调用方只要在同一轮微任务内标记，
 * 同一次失败就不会再被弹一次；用于替代「内联提示 + 兜底 toast」并存的双提示写法。
 *
 * @param error 已捕获的错误对象。
 */
export function suppressApiErrorToast(error) {
    markApiErrorHandled(error);
}
/** 可选的非错误提示器，避免 shared 层依赖具体 UI 组件库。 */
let apiToastNotifier = null;
/** 配置全局成功/警告/信息提示，由 app 层在启动阶段注入全局提示实现。 */
export function configureApiToastNotifier(notifier) {
    apiToastNotifier = notifier;
}
/**
 * 非错误提示的投递实现：空文案与未注册提示实现都静默跳过。
 *
 * 提示是纯展示行为，缺失不应影响业务结果，因此这里不抛错也不兜底上报。
 *
 * @param level 展示级别。
 * @param message 提示文案。
 */
function deliverToast(level, message) {
    const text = message.trim();
    if (text === '')
        return;
    apiToastNotifier?.(level, text);
}
/**
 * 全站统一的成功提示出口。
 *
 * 保存、创建、删除、上传等写操作成功后统一调用它，而不是各自调用 message.success：
 * 出口唯一，重叠触发时由出口按文案去重，同一句提示不会在短时间内弹多次。
 *
 * @param message 成功文案；空串按未提供处理，不弹提示。
 */
export function notifyApiSuccess(message) {
    deliverToast('success', message);
}
/**
 * 全站统一的警告提示出口。
 *
 * 本地校验、门禁拦截等「不是接口失败但需要提醒」的场景统一调用它，
 * 与成功/失败提示共用同一个出口与去重窗口，避免同一句提醒被弹多次。
 *
 * @param message 警告文案；空串按未提供处理，不弹提示。
 */
export function notifyApiWarning(message) {
    deliverToast('warning', message);
}
/**
 * 全站统一的信息提示出口。
 *
 * 用于演示态等中性说明（非成功也非失败），与其它提示共用同一个出口与去重窗口。
 *
 * @param message 信息文案；空串按未提供处理，不弹提示。
 */
export function notifyApiInfo(message) {
    deliverToast('info', message);
}
/** 浏览器上传 FormData 时必须由浏览器自行写入 multipart boundary。 */
function isFormDataPayload(data) {
    return typeof FormData !== 'undefined' && data instanceof FormData;
}
/** 为请求添加认证头和稳定的默认配置。 */
function prepareRequest(config) {
    const token = authTokenReader();
    if (token)
        config.headers.set('Authorization', `Bearer ${token}`);
    if (isFormDataPayload(config.data)) {
        config.headers.delete('Content-Type');
    }
    return config;
}
/** 创建可测试、可复用的 Axios 实例，不在调用方重复创建客户端。 */
export function createHttpClient(options = {}) {
    const client = axios.create({
        baseURL: appEnv.apiBaseUrl,
        timeout: appEnv.apiTimeoutMs,
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    client.interceptors.request.use(prepareRequest);
    client.interceptors.response.use((response) => {
        /** 后端业务失败统一走 HTTP 200 外壳，这里按业务状态码归一为请求错误。 */
        const envelope = response.data;
        if (envelope && typeof envelope.code === 'number' && envelope.code !== API_SUCCESS_CODE) {
            const isSessionExpired = envelope.code === API_UNAUTHORIZED_CODE;
            const normalizedError = new ApiError({
                kind: 'http',
                message: envelope.msg ?? envelope.message ?? '请求失败，请稍后重试',
                status: isSessionExpired ? 401 : undefined,
                code: String(envelope.code),
                details: envelope,
            });
            if (isSessionExpired)
                unauthorizedHandler?.();
            notifyApiErrorIfUnhandled(normalizedError);
            return Promise.reject(normalizedError);
        }
        return response;
    }, (error) => {
        const normalizedError = normalizeApiError(error);
        if (normalizedError.status === 401)
            unauthorizedHandler?.();
        notifyApiErrorIfUnhandled(normalizedError);
        return Promise.reject(normalizedError);
    });
    return client;
}
/** 应用共享的 Axios 客户端，领域模块只通过它发起请求。 */
export const httpClient = createHttpClient();
